import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { ciSearch, officesFor, removeWindow, saveWindow, windowFormQuery, type CiHit, type Office, type WindowForm } from '../api/maintenance'
import { ApiError } from '../api/client'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { Spinner } from '../ui/Spinner'
import './WindowFormModal.css'

/**
 * Formularul unei ferestre - `views/version-3/inventoree/maintenance/window_form.php`
 * + `Maintenance::windowForm/saveWindow/removeWindow/officesFor/ciSearch`.
 *
 * Aceleasi campuri, in aceeasi ordine: nume, client, acoperire (cu clasa sau
 * elementul cautat), sediile, de la / pana la, cererea de schimbare (niciuna,
 * una existenta, sau una deschisa acum). Sediile se reiau cand se schimba
 * clientul, ca sa nu se ofere spre bifare sedii ale altcuiva.
 *
 * Dupa salvare, ecranul de sub el isi redeseneaza singur continutul
 * (`onSaved`), nu se reincarca pagina - ca in portal.
 */

/** `datetime-local` vrea `YYYY-MM-DDTHH:MM`; baza da `YYYY-MM-DD HH:MM:SS`. */
function toLocal(value: string): string {
  return value === '' ? '' : value.slice(0, 16).replace(' ', 'T')
}

const pad = (n: number) => String(n).padStart(2, '0')

/** Ziua unei date locale, `YYYY-MM-DD`. */
function dayOf(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Cele doua jumatati ale unui moment: ziua si ora, fiecare cu campul ei.
 *
 * Vezi `WhenField`. Un moment gol da doua jumatati goale, si unul caruia ii
 * lipseste ora da ora goala - nu se inventeaza niciuna.
 */
export function splitLocal(value: string): [string, string] {
  const local = toLocal(value)
  const t = local.indexOf('T')
  return t < 0 ? [local, ''] : [local.slice(0, t), local.slice(t + 1, t + 6)]
}

/**
 * Inapoi in EXACT ce trimitea `datetime-local`, si nimic altceva.
 *
 * Gol cat timp ii lipseste o jumatate, fiindca asta intoarce si controlul
 * nativ: "a field left half-typed - a date with no time - posts as nothing"
 * (`views/version-3/inventoree/maintenance/window_form.php`). Serverul vede
 * deci acelasi sir ca inainte, iar `DbalMaintenanceWindowSource::datetime()`
 * primeste tot `YYYY-MM-DDTHH:MM` si scrie tot `Y-m-d H:i:s`.
 */
export function joinLocal(day: string, time: string): string {
  return day === '' || time === '' ? '' : `${day}T${time.slice(0, 5)}`
}

/**
 * Ora cu care se deschide o fereastra NOUA.
 *
 * `day` gol inseamna ca nimeni n-a aratat spre o zi: butonul Add de pe lista
 * (`/app/maintenance`) si cel de pe calendar deschid amandoua formularul asa.
 * Pana pe 17 septembrie 2026 cazul ala intorcea `['', '']`, deci formularul se
 * deschidea cu AMANDOUA campurile goale si omul trebuia sa scrie si ziua si
 * ora. Macheta veche facea dinadins altfel, si scrie de ce chiar in ea: "A new
 * window always opens with hours in it (...) Today when the form was opened
 * from the list". Ziua de azi se intoarce aici; ora ramane cea aleasa de v4
 * (22:00, plus patru ore), ca ferestrele deschise din calendar sa nu-si schimbe
 * obiceiul.
 */
export function defaultRange(day: string, hour: number | null, today: Date = new Date()): [string, string] {
  const from = day !== '' ? day : dayOf(today)
  const h = hour ?? 22
  const start = `${from}T${pad(h)}:00`
  const end = new Date(`${from}T${pad(h)}:00:00`)
  end.setHours(end.getHours() + 4)
  return [start, `${dayOf(end)}T${pad(end.getHours())}:${pad(end.getMinutes())}`]
}

/**
 * Orele din lista de alegere: din sfert in sfert de ora, 00:00 ... 23:45.
 *
 * Macheta veche a ferestrei nu avea lista - lasa ora in seama lui
 * `datetime-local` - dar celelalte ecrane de ora din portal (rapoartele,
 * instalarea programata) foloseau wickedpicker, pe 24 de ore. Aici e tot pe 24
 * de ore; sfertul de ora e pasul pe care il poate avea o noapte de lucru, iar o
 * ora de alt fel (22:10) se SCRIE in casuta, ca pana acum.
 */
export const TIME_CHOICES: string[] = Array.from({ length: 96 }, (_, i) => `${pad(Math.floor(i / 4))}:${pad((i % 4) * 15)}`)

/**
 * Ce a scris omul in casuta orei, adus la `HH:MM` - sau `''` daca nu e o ora.
 *
 * Primeste `22:30`, `9:05`, `2230`, `22` (= 22:00). Gol cand nu e o ora
 * adevarata (`25:00`, `ab`): exact ce intorcea si `<input type="time">` pe un
 * camp neterminat, deci ce se posteaza nu se schimba.
 */
export function parseTime(text: string): string {
  const m = /^\s*(\d{1,2})(?::?(\d{2}))?\s*$/.exec(text)
  if (!m) return ''
  const h = Number(m[1])
  const min = m[2] === undefined ? 0 : Number(m[2])
  return h > 23 || min > 59 ? '' : `${pad(h)}:${pad(min)}`
}

/**
 * Sfarsitul mutat cu cat s-a mutat inceputul.
 *
 * Pe 18 septembrie 2026 ecranul arata From `09/09/2026 22:00`, To
 * `09/19/2026 02:00` - o fereastra de zece zile. Nu era o socoteala gresita:
 * `defaultRange` dase, deschis din Add pe 18, 18 22:00 -> 19 02:00, iar ziua
 * de la From fusese schimbata pe 9 in campul ei. To n-avea de unde sa afle, si
 * a ramas pe 19. Asa se poarta si calendarele obisnuite: muti inceputul, se
 * muta si sfarsitul, iar fereastra isi pastreaza lungimea. To se poate schimba
 * singur dupa aceea, oricat.
 *
 * `from` si `to` sunt momente intregi (`YYYY-MM-DDTHH:MM`); unul gol nu muta
 * nimic.
 */
export function shiftEnd(from: string, to: string, end: [string, string]): [string, string] {
  const endAt = joinLocal(end[0], end[1])
  if (from === '' || to === '' || endAt === '' || from === to) return end
  const moved = new Date(new Date(`${endAt}:00`).getTime() + (new Date(`${to}:00`).getTime() - new Date(`${from}:00`).getTime()))
  if (Number.isNaN(moved.getTime())) return end
  return [dayOf(moved), `${pad(moved.getHours())}:${pad(moved.getMinutes())}`]
}

/**
 * ORA: O CASUTA IN CARE SE SCRIE SI O LISTA PE CARE O DESCHIDE CODUL NOSTRU.
 *
 * Livrarea 26.9.18.2 pusese aici `<input type="time">` si un buton care chema
 * `showPicker()`, pe presupunerea ca browserul are un alegator de ora. Firefox
 * pe desktop NU are: `type="time"` e acolo numai segmente de scris, fara nicio
 * iconita si fara niciun panou (se vede in captura din 18 septembrie 2026 -
 * ziua are iconita de calendar in ea, ora nu). Butonul nu deschidea deci nimic.
 *
 * Acum nu se mai sprijina nimic pe browser: casuta e text simplu, iar lista de
 * ore e un `.dropdown-menu` din Bootstrap desenat de componenta asta. Se
 * deschide la clic pe casuta, la clic pe ceas si cu sageata in jos; se alege
 * cu mouse-ul sau cu sagetile si Enter; se inchide cu Escape, cu clic in afara
 * sau dupa alegere. Scrisul merge ca inainte (`parseTime`).
 *
 * Ceasul sta intr-un `.input-group-btn`, nu e el insusi `.input-group-addon`:
 * vezi `WindowFormModal.css`, de ce cadea sub casuta.
 */
function TimeField({ label, value, onChange }: { label: string; value: string; onChange(next: string): void }) {
  const [text, setText] = useState(value)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const root = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLUListElement>(null)

  /* Ce scrie casuta: textul omului cat timp spune aceeasi ora ca valoarea (sau
     nicio ora, cand valoarea e goala); altfel valoarea venita din afara - de
     pilda To, mutat odata cu From. */
  const shown = parseTime(text) === value ? text : value

  /* Randul de pornire al listei: ora din casuta, sau sfertul de sub ea. */
  const nearest = (time: string) => {
    if (time === '') return -1
    const [h, m] = time.split(':').map(Number)
    return h * 4 + Math.floor(m / 15)
  }

  const show = () => {
    if (open) return
    setActive(nearest(value))
    setOpen(true)
  }

  const choose = (time: string) => {
    setText(time)
    onChange(time)
    setOpen(false)
    input.current?.focus()
  }

  /* Clic in afara inchide lista. */
  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])

  /* Randul ales se vede in lista, nu ramane ascuns sub primele ore. */
  useEffect(() => {
    if (!open || active < 0 || !list.current) return
    const row = list.current.children[active] as HTMLElement | undefined
    if (row) list.current.scrollTop = row.offsetTop - list.current.clientHeight / 2
  }, [open, active])

  return (
    <div className={`input-group mw-when-part mw-time${shown !== '' && parseTime(shown) === '' ? ' has-error' : ''}`} ref={root}>
      <input
        ref={input}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="HH:MM"
        maxLength={5}
        className="form-control"
        role="combobox"
        aria-label={`${label}: the time`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-invalid={shown !== '' && parseTime(shown) === ''}
        value={shown}
        onClick={show}
        onChange={(e) => {
          const next = parseTime(e.target.value)
          setText(e.target.value)
          onChange(next)
          if (open && next !== '') setActive(nearest(next))
        }}
        onBlur={() => {
          if (parseTime(shown) !== '') setText(parseTime(shown))
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            if (!open) show()
            else setActive((i) => Math.min(TIME_CHOICES.length - 1, i + 1))
          } else if (e.key === 'ArrowUp' && open) {
            e.preventDefault()
            setActive((i) => Math.max(0, i - 1))
          } else if (e.key === 'Enter' && open && active >= 0) {
            e.preventDefault()
            choose(TIME_CHOICES[active])
          } else if (e.key === 'Escape' && open) {
            e.preventDefault()
            e.stopPropagation()
            setOpen(false)
          } else if (e.key === 'Tab') {
            setOpen(false)
          }
        }}
      />
      <span className="input-group-btn">
        <button
          type="button"
          className="btn btn-default"
          aria-label={`${label}: pick the time`}
          aria-expanded={open}
          onClick={() => {
            if (open) setOpen(false)
            else {
              show()
              input.current?.focus()
            }
          }}
        >
          <i className="far fa-clock" aria-hidden="true"></i>
        </button>
      </span>
      {open && (
        <ul ref={list} className="dropdown-menu mw-time-list" role="listbox" aria-label={`${label}: times`}>
          {TIME_CHOICES.map((time, i) => (
            <li key={time} role="option" aria-selected={time === value} className={i === active ? 'active' : undefined}>
              <a
                href="#"
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault()
                  choose(time)
                }}
              >
                {time}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * ZIUA SI ORA, FIECARE CU CAMPUL EI.
 *
 * Ziua ramane `<input type="date">`: acolo browserul chiar are calendar, si il
 * arata cu iconita lui IN casuta (Firefox, Chrome, Edge). Butonul de calendar
 * pus sub ea in 26.9.18.2 facea acelasi lucru a doua oara, deci a plecat.
 *
 * Ora e `TimeField`, de mai sus.
 *
 * CE SE SALVEAZA NU SE SCHIMBA: cele doua jumatati se lipesc la loc cu
 * `joinLocal` in acelasi `YYYY-MM-DDTHH:MM` pe care il trimitea
 * `datetime-local`, sub aceleasi nume `starts_at` / `ends_at`, iar campul
 * ascuns de langa ele poarta in pagina fix sirul care pleaca.
 */
function WhenField({ label, name, value, onChange }: {
  label: string
  name: string
  value: [string, string]
  onChange(next: [string, string]): void
}) {
  const [day, time] = value

  return (
    <div className="row mw-when">
      <div className="col-xs-6">
        <input
          type="date"
          className="form-control"
          aria-label={`${label}: the day`}
          value={day}
          onChange={(e) => onChange([e.target.value, time])}
        />
      </div>
      <div className="col-xs-6">
        <TimeField label={label} value={time} onChange={(next) => onChange([day, next])} />
      </div>
      <input type="hidden" name={name} value={joinLocal(day, time)} />
    </div>
  )
}

export interface WindowFormModalProps {
  open: boolean
  /** 0 = fereastra noua. */
  id: number
  day?: string
  hour?: number | null
  onClose(): void
  onSaved(message: string, warning: boolean): void
}

export function WindowFormModal({ open, id, day = '', hour = null, onClose, onSaved }: WindowFormModalProps) {
  const form = useQuery({ ...windowFormQuery(id, day, hour), enabled: open })

  return (
    <Modal id="windowModal" open={open} onClose={onClose} title={id > 0 ? 'Edit the window' : 'Plan a window'}>
      {form.error instanceof ApiError && form.error.status === 404 && <div className="alert alert-danger">That window no longer exists.</div>}
      {form.error && !(form.error instanceof ApiError && form.error.status === 404) && (
        <div className="alert alert-danger">Something went wrong: {form.error.message}</div>
      )}
      {!form.data && !form.error && <Spinner />}
      {form.data && <WindowFields key={`${id}-${day}-${hour ?? ''}`} data={form.data} id={id} day={day} hour={hour} onClose={onClose} onSaved={onSaved} />}
    </Modal>
  )
}

function WindowFields({ data, id, day, hour, onClose, onSaved }: { data: WindowForm; id: number; day: string; hour: number | null; onClose(): void; onSaved(m: string, w: boolean): void }) {
  const client = useQueryClient()
  const w = data.window
  const [range] = useState(() => defaultRange(day, hour))
  const [name, setName] = useState(w?.name ?? '')
  const [company, setCompany] = useState(String(w?.company_id ?? data.companies[0]?.ID ?? ''))
  const [scopeType, setScopeType] = useState(String(w?.scope_type ?? 1))
  const [classId, setClassId] = useState(w && w.scope_type === 2 ? String(w.scope_id) : '')
  const [ciId, setCiId] = useState(data.scopeCi?.id ?? 0)
  const [ciText, setCiText] = useState(data.scopeCi?.name ?? '')
  const [hits, setHits] = useState<CiHit[]>([])
  const [offices, setOffices] = useState<Office[]>(data.offices)
  const [picked, setPicked] = useState<string[]>(data.pickedOffices.map(String))
  const [starts, setStarts] = useState<[string, string]>(() => splitLocal(w ? w.starts_at : range[0]))
  const [ends, setEnds] = useState<[string, string]>(() => splitLocal(w ? w.ends_at : range[1]))
  /* Ultimul inceput intreg, ca To sa se mute cu From - vezi `shiftEnd`. Tinut
     separat de `starts`, fiindca in timp ce se scrie ora trece si prin gol, iar
     mutarea se socoteste de la ultimul moment adevarat, nu de la gol. */
  const lastStart = useRef(joinLocal(starts[0], starts[1]))
  const [ticketMode, setTicketMode] = useState(w && w.ticket_id > 0 ? 'existing' : 'none')
  const [ticketId, setTicketId] = useState(w && w.ticket_id > 0 ? String(w.ticket_id) : '')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [armed, setArmed] = useState(false)
  const searchTimer = useRef<number | null>(null)

  /* Sediile clientului ales, reluate cand se schimba clientul. */
  useEffect(() => {
    const co = Number(company)
    if (!(co > 0) || co === (w?.company_id ?? data.companies[0]?.ID)) return
    let alive = true
    officesFor(co)
      .then((list) => {
        if (!alive) return
        setOffices(list)
        setPicked([])
      })
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [company, w?.company_id, data.companies])

  function moveStart(next: [string, string]) {
    setStarts(next)
    const at = joinLocal(next[0], next[1])
    if (at === '') return
    const from = lastStart.current
    lastStart.current = at
    setEnds((end) => shiftEnd(from, at, end))
  }

  function search(term: string) {
    setCiText(term)
    setCiId(0)
    if (searchTimer.current !== null) window.clearTimeout(searchTimer.current)
    if (term.trim().length < 2) {
      setHits([])
      return
    }
    searchTimer.current = window.setTimeout(() => {
      ciSearch(term, Number(company) || 0)
        .then(setHits)
        .catch(() => setHits([]))
    }, 250)
  }

  async function save() {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const out = await saveWindow({
        ID: id,
        name,
        company_id: company,
        starts_at: joinLocal(starts[0], starts[1]),
        ends_at: joinLocal(ends[0], ends[1]),
        scope_type: scopeType,
        scope_id: scopeType === '2' ? classId : scopeType === '3' ? String(ciId) : '',
        offices: picked,
        ticket_mode: ticketMode,
        ticket_id: ticketMode === 'existing' ? ticketId : '',
      })
      if (out.status !== 'OK') {
        setError(out.message)
        return
      }
      void client.invalidateQueries({ queryKey: ['maintenance'] })
      onSaved(out.message, out.warning === true)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The request failed.')
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!armed) {
      setArmed(true)
      window.setTimeout(() => setArmed(false), 4000)
      return
    }
    setBusy(true)
    try {
      const out = await removeWindow(id)
      if (out.status !== 'OK') {
        setError(out.message)
        return
      }
      void client.invalidateQueries({ queryKey: ['maintenance'] })
      toast(out.message, 'Maintenance windows', 'success')
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <form id="windowForm" className="form-horizontal" onSubmit={(e) => e.preventDefault()}>
          {error !== '' && <div className="alert alert-danger">{error}</div>}
          <div className="form-group">
            <label className="col-sm-3 control-label">Name</label>
            <div className="col-sm-9">
              <input type="text" className="form-control" name="name" maxLength={190} value={name} onChange={(e) => setName(e.target.value)} placeholder="Patch night, core banking" />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label">Customer</label>
            <div className="col-sm-9">
              <SelectPicker id="windowCompany" name="company_id" title="Choose…" value={company} onChange={setCompany} options={data.companies.map((c) => ({ value: String(c.ID), label: c.Nume }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label">Covers</label>
            <div className="col-sm-9">
              <SelectPicker id="windowScopeType" name="scope_type" title="" value={scopeType} onChange={setScopeType} options={data.scopes.map((s) => ({ value: String(s.value), label: s.label }))} />
              <p className="help-block" style={{ marginBottom: 0 }}>The narrowest window wins: one item beats a class, a class beats the whole customer.</p>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label">Offices</label>
            <div className="col-sm-9">
              <SelectPicker id="windowOffices" name="offices[]" title="No office selected" multiple value={picked} onChange={setPicked} options={offices.map((o) => ({ value: String(o.ID), label: o.Nume }))} />
              <p className="help-block" style={{ marginBottom: 0 }}>With two or more offices, a child ticket is opened for each one.</p>
            </div>
          </div>
          {scopeType === '2' && (
            <div className="form-group" id="windowClassRow">
              <label className="col-sm-3 control-label">Class</label>
              <div className="col-sm-9">
                <SelectPicker id="windowClass" title="Choose…" value={classId} onChange={setClassId} options={data.classes.map((c) => ({ value: String(c.ID), label: c.name }))} />
              </div>
            </div>
          )}
          {scopeType === '3' && (
            <div className="form-group" id="windowCiRow">
              <label className="col-sm-3 control-label">Item</label>
              <div className="col-sm-9">
                <input type="text" className="form-control" id="windowCiSearch" autoComplete="off" placeholder="Type two letters of the name or serial" value={ciText} onChange={(e) => search(e.target.value)} />
                <input type="hidden" id="windowCiId" name="scope_id" value={ciId} />
                {hits.length > 0 && (
                  <div className="list-group" id="windowCiResults" style={{ marginTop: 4, marginBottom: 0 }}>
                    {hits.map((hit) => (
                      <a
                        key={hit.id}
                        href="#"
                        className="list-group-item"
                        onClick={(e) => {
                          e.preventDefault()
                          setCiId(hit.id)
                          setCiText(hit.name)
                          setHits([])
                        }}
                      >
                        {hit.name} <span className="adm-muted">{hit.class}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="form-group">
            <label className="col-sm-3 control-label">From</label>
            <div className="col-sm-9">
              <WhenField label="From" name="starts_at" value={starts} onChange={moveStart} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label">To</label>
            <div className="col-sm-9">
              <WhenField label="To" name="ends_at" value={ends} onChange={setEnds} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label">Change request</label>
            <div className="col-sm-9">
              <SelectPicker
                id="windowTicketMode"
                name="ticket_mode"
                title=""
                value={ticketMode}
                onChange={setTicketMode}
                options={[
                  { value: 'none', label: 'None' },
                  { value: 'existing', label: 'Point at an existing ticket' },
                  { value: 'new', label: 'Open one now' },
                ]}
              />
              <p className="help-block" style={{ marginBottom: 0 }}>A ticket opened here is planned for the start of the window and lists what it covers.</p>
            </div>
          </div>
          {ticketMode === 'existing' && (
            <div className="form-group" id="windowTicketIdRow">
              <label className="col-sm-3 control-label">Ticket number</label>
              <div className="col-sm-9">
                <input type="number" className="form-control" name="ticket_id" min={1} value={ticketId} onChange={(e) => setTicketId(e.target.value)} />
              </div>
            </div>
          )}
      </form>
      <div className="modal-footer" style={{ margin: '15px -15px -15px', padding: '15px' }}>
        {id > 0 && (
          <button type="button" className="btn btn-danger pull-left" id="windowRemoveOne" disabled={busy} onClick={() => void remove()}>
            <i className="fas fa-trash"></i> {armed ? 'Sure? remove' : 'Remove'}
          </button>
        )}
        <button type="button" className="btn btn-default" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" id="windowSave" disabled={busy} onClick={() => void save()}>
          Save
        </button>
      </div>
    </>
  )
}
